import type {
    CategorizationRule,
    ReportConfig,
    RuleCondition,
    RuleNode,
    RuleSet,
    MetricSection,
    DataColumn,
    ModelKey,
} from "../types";
import type { ReportLogicContext } from "./context";
import {
    insideRelatedPredicate,
    needsRelatedPredicate,
    relatedTableKey,
    RELATED_POSITIVE_OPERATORS,
    qualifiedRelatedPredicate,
    relatedExistsSql,
    qualifiedConditionColumn,
    relatedResultTextSql,
} from "./relatedTables";
import {
    findReportColumn,
    targetFieldDef,
    allColumnsForModel,
    columnMatchesKey,
    columnsMatch,
    columnsForModel,
    resolveCountDistinctColumn,
    dataColumnSelectionKey,
    findModelColumn,
    isRuleValueSourceEligible,
} from "./catalogResolution";
import {
    comparisonSqlForColumn,
    sqlList,
    isDateLikeColumn,
    dateSqlLiteral,
    idSqlList,
    inClause,
    notInClause,
    sqlLikeLiteral,
    sqlCommentText,
    sqlLiteral,
    safeSqlAlias,
    buildLateConditionSql,
    minutesBetweenSql,
    columnComparisonSql,
    resultTextSqlForColumn,
    conditionBlankValue,
    leftComparisonSql,
    nullFallbackFor,
    nullFallbackIssue,
    todayComparisonSql,
    wrapNullFallback,
} from "./sqlExpressions";
import { resolveOptionIds } from "./lookupResolution";
import {
    isRuleGroupNode,
    ruleNodeJoin,
    splitRuleAndRuns,
    ruleHasNestedGroup,
    ruleLeafConditions,
    MAX_RULE_GROUP_DEPTH,
    simulateRuleMatches,
} from "./rules";
import { ruleOpDef, columnDataTypeCategory, isOperatorAllowedForColumn as catalogOperatorAllowed } from "./operatorCatalog";
import { effectiveComparisonTier, resolveDifferenceUnit } from "./temporalPrecision";
// Circular with ./periodDate (which uses the rule-tree helpers here); safe because both
// sides only call each other inside functions, never at module evaluation time.
import { isPeriodDateReady } from "./periodDate";
import { selectedDetailRuleSetIds, usesDetailRuleSetList } from "./detailRuleSetSelection";
import { usesCombinedRuleSets } from "./combinedRuleSetSelection";
// Circular with ./combinedRuleSets (which uses the rule helpers here); safe because both
// sides only call each other inside functions, never at module evaluation time.
import { isCombinedRuleSetsReady } from "./combinedRuleSets";
import { detailCalculatedColumns, measureIssue } from "./calculatedFields";
import { detailConstantIssues } from "./detailConstantColumns";

// --- "Set value" results ------------------------------------------------------------
// A Set rule is one WHEN branch:  WHEN <conditions> THEN <result>.  <result> is a typed
// value, or - with valueSource "column" - another column's value on the same row. Every
// SQL emitter goes through ruleResultSql / setRuleMatchExpression so the CASE builders,
// the "hide unmatched" filter and the exclusion explanations agree. Mirrors
// ReportSqlBuilder.RuleResults.cs.

function ruleValueFromColumn(rule: CategorizationRule) {
    return rule.valueSource === "column";
}

/**
 * The rule's TYPED value, or "" for a column-sourced rule (whose leftover valueCustom /
 * valueFromList must never be written). Code that enumerates known categories (chart series
 * suggestions, summaries) relies on this: a column-sourced rule has no fixed category.
 * For "does this rule write anything" use ruleHasResult; for display use ruleResultLabel.
 */
function ruleResultValue(rule: CategorizationRule) {
    if (ruleValueFromColumn(rule)) return "";
    return (rule.valueCustom || "").trim() || rule.valueFromList || "";
}

/**
 * The model a rule set's CASE is built on: the first condition column that resolves, then a
 * real target column, then a value-source column, else the report's first model.
 */
function ruleSetModelKey(context: ReportLogicContext, report: ReportConfig, ruleSet: RuleSet, fallbackToFirstModel = true): ModelKey | "" {
    for (const rule of ruleSet.rules) {
        // Flatten the whole tree - a nested group's conditions are just as valid a
        // source for resolving which data model this rule set belongs to.
        for (const condition of ruleLeafConditions(rule.conditions)) {
            const column = condition.column ? findReportColumn(context, report, condition.column) : null;
            if (column?.model) return column.model;
        }
        if (rule.targetField && rule.targetField !== "category") {
            const target = findReportColumn(context, report, rule.targetField);
            if (target?.model) return target.model;
        }
        if (ruleValueFromColumn(rule) && rule.valueColumn) {
            const source = findReportColumn(context, report, rule.valueColumn);
            if (source?.model) return source.model;
        }
    }
    return fallbackToFirstModel ? report.models[0] || "" : "";
}

function ruleSetForRule(report: ReportConfig, rule: CategorizationRule) {
    return report.bucketRuleSets.find((set) => set.rules.some((item) => item.id === rule.id));
}

/** The model a single rule belongs to: its rule set's model when it is in one. */
function ruleModelKey(context: ReportLogicContext, report: ReportConfig, rule: CategorizationRule): ModelKey | "" {
    const ruleSet = ruleSetForRule(report, rule);
    return ruleSetModelKey(context, report, ruleSet || { id: "", name: "", includeElse: true, elseLabel: "", rules: [rule] });
}

/**
 * The source column of a column-sourced rule, resolved on the rule set's OWN model and only
 * if isRuleValueSourceEligible allows it - otherwise null, and the rule emits nothing.
 * valueColumn is client-supplied; this lookup is the only thing that lets it into SQL.
 */
function ruleValueColumn(context: ReportLogicContext, report: ReportConfig, rule: CategorizationRule, modelKey?: ModelKey | "") {
    if (!ruleValueFromColumn(rule) || !rule.valueColumn) return null;
    const model = modelKey === undefined ? ruleModelKey(context, report, rule) : modelKey;
    const column = model ? findModelColumn(context, model, rule.valueColumn) : findReportColumn(context, report, rule.valueColumn);
    return column && isRuleValueSourceEligible(column) ? column : null;
}

/** True when a Set rule has something to write: a typed value, or a usable source column. */
function ruleHasResult(context: ReportLogicContext, report: ReportConfig, rule: CategorizationRule, modelKey?: ModelKey | "") {
    return ruleValueFromColumn(rule) ? !!ruleValueColumn(context, report, rule, modelKey) : !!ruleResultValue(rule);
}

/** The THEN operand: a quoted typed value, or the source column rendered as text. */
function ruleResultSql(context: ReportLogicContext, report: ReportConfig, rule: CategorizationRule, modelKey?: ModelKey | "") {
    if (ruleValueFromColumn(rule)) {
        const column = ruleValueColumn(context, report, rule, modelKey);
        return column ? relatedResultTextSql(column) : null;
    }
    const value = ruleResultValue(rule);
    return value ? sqlLiteral(value) : null;
}

/** What the rule writes, for summaries and sentences: the typed value, or "the value of <column>". */
function ruleResultLabel(context: ReportLogicContext, report: ReportConfig, rule: CategorizationRule) {
    if (!ruleValueFromColumn(rule)) return ruleResultValue(rule);
    const column = ruleValueColumn(context, report, rule);
    return column ? `the value of ${column.label}` : "";
}

/**
 * The synthetic "source column has a value" leaf of a column-sourced rule. Keyed by the
 * resolved column's catalog id, so it cannot resolve to a same-named column elsewhere.
 */
function ruleValueNotBlankCondition(column: DataColumn): RuleCondition {
    return { id: "value-not-blank", column: dataColumnSelectionKey(column), operator: "not_empty", values: [], text: "" };
}

/**
 * The WHEN of a Set rule. A column-sourced rule also requires its source column to have a
 * value, so a blank row falls through to the next rule and finally to the ELSE rather than
 * becoming an empty category. For a typed-value rule this is exactly ruleConditionExpression.
 */
function setRuleMatchExpression(context: ReportLogicContext, report: ReportConfig, rule: CategorizationRule, useIds = false, modelKey?: ModelKey | "") {
    const expression = ruleConditionExpression(context, report, rule, useIds);
    if (!expression || !ruleValueFromColumn(rule)) return expression;
    const column = ruleValueColumn(context, report, rule, modelKey);
    const guard = column ? ruleConditionSql(context, report, ruleValueNotBlankCondition(column), useIds) : null;
    return guard ? `((${expression}) AND ${guard})` : null;
}

// One condition, read through its related table when it has to be (logic/relatedTables.ts,
// mirrors RuleConditionSql in ReportSqlBuilder.RelatedTables.cs).
function ruleConditionSql(context: ReportLogicContext, report: ReportConfig, condition: RuleCondition, useIds = false) {
    if (insideRelatedPredicate() || !condition.column) return ruleConditionSqlCore(context, report, condition, useIds);
    const column = findReportColumn(context, report, condition.column);
    const compare = (condition.leftKind === "minutes_between" || condition.compareTo === "column") && condition.compareColumn
        ? findReportColumn(context, report, condition.compareColumn)
        : null;
    const related = [column, compare].filter((item) => needsRelatedPredicate(item)) as DataColumn[];
    if (!related.length) return ruleConditionSqlCore(context, report, condition, useIds);
    const tables = Array.from(new Set(related.map(relatedTableKey)));
    if (tables.length > 1) return null; // two different related tables can't be compared (the server refuses it)
    const positive = RELATED_POSITIVE_OPERATORS[condition.operator];
    if (positive) {
        const sql = qualifiedRelatedPredicate(tables[0], () => ruleConditionSqlCore(context, report, { ...condition, operator: positive } as RuleCondition, useIds));
        return sql ? relatedExistsSql(related[0], sql, true) : null;
    }
    const sql = qualifiedRelatedPredicate(tables[0], () => ruleConditionSqlCore(context, report, condition, useIds));
    return sql ? relatedExistsSql(related[0], sql, false) : null;
}

function ruleConditionSqlCore(context: ReportLogicContext, report: ReportConfig, condition: RuleCondition, useIds = false) {
    if (!condition.column) return null;
    const column = qualifiedConditionColumn(findReportColumn(context, report, condition.column));
    if (!column) return null;

    // --- Operand modes -------------------------------------------------------------
    // Both branches resolve their SECOND column through findReportColumn and return null
    // when it does not resolve. condition.compareColumn arrives from client-supplied JSON,
    // and that catalog lookup is the only thing that makes it safe to put in SQL - it is
    // never interpolated as received. Mirrors RuleConditionSql in ReportSqlBuilder.cs.
    if (condition.leftKind === "minutes_between") {
        const toColumn = condition.compareColumn ? qualifiedConditionColumn(findReportColumn(context, report, condition.compareColumn)) : null;
        if (!toColumn) return null;
        return minutesComparisonSql(minutesBetweenSql(column, toColumn, condition.absoluteMinutes, condition.differenceUnit), condition);
    }
    // "If blank, use <value>": ISNULL/COALESCE(column, value), type-checked against the column
    // (see nullFallbackFor), or null when none applies - which is every condition saved before
    // it existed, so the branches below are unchanged. Mirrors ReportSqlBuilder.NullFallback.cs.
    const fallback = nullFallbackFor(condition, column);
    if (condition.compareTo === "today") {
        return isDateLikeColumn(column) ? todayComparisonSql(column, condition.operator, fallback) : null;
    }
    if (condition.compareTo === "column") {
        const rightColumn = condition.compareColumn ? qualifiedConditionColumn(findReportColumn(context, report, condition.compareColumn)) : null;
        if (!rightColumn) return null;
        return columnComparisonSql(column, rightColumn, condition.operator, condition.compareGranularity, fallback);
    }

    // With a fallback, every arm - including the "keep blank rows" IS NULL arms of the negated
    // operators - sees the fallback, so a blank row is judged by the fallback value.
    const sql = wrapNullFallback(column.sql, fallback);
    const compareSql = leftComparisonSql(column, fallback);
    const numeric = (value: string) => /^-?\d+(\.\d+)?$/.test(String(value)) ? value : sqlList([value]);
    const comparisonValue = (value: string) => isDateLikeColumn(column) ? dateSqlLiteral(value) : numeric(value);
    const textSql = `CONVERT(NVARCHAR(MAX), ${sql})`;
    const idEq = (values: string[], negate: boolean) => {
        if (!useIds || !column.idSql) return null;
        const ids = resolveOptionIds(context, column.model, column.key, values);
        if (!ids) return null;
        return negate ? `(${column.idSql} IS NULL OR ${column.idSql} <> ${idSqlList(ids)})` : `${column.idSql} = ${idSqlList(ids)}`;
    };
    switch (condition.operator) {
        case "in": return condition.values.length ? inClause(context, column.model, column, condition.values, useIds, fallback ? compareSql : null) : null;
        case "not_in": return condition.values.length ? notInClause(context, column.model, column, condition.values, useIds, fallback ? compareSql : null) : null;
        case "eq": return condition.values.length ? (fallback ? null : idEq(condition.values.slice(0, 1), false)) || `${compareSql} = ${sqlList(condition.values.slice(0, 1))}` : null;
        case "ne": return condition.values.length ? (fallback ? null : idEq(condition.values.slice(0, 1), true)) || `(${compareSql} IS NULL OR ${compareSql} <> ${sqlList(condition.values.slice(0, 1))})` : null;
        case "contains": return condition.text ? `${textSql} LIKE ${sqlLikeLiteral(condition.text, "contains")}` : null;
        case "not_contains": return condition.text ? `(${textSql} NOT LIKE ${sqlLikeLiteral(condition.text, "contains")} OR ${sql} IS NULL)` : null;
        case "starts_with": return condition.text ? `${textSql} LIKE ${sqlLikeLiteral(condition.text, "starts_with")}` : null;
        // The three negated forms below are NULL-safe on purpose, exactly as not_contains is:
        // a NULL column makes LIKE / BETWEEN evaluate to UNKNOWN, and NOT UNKNOWN is still
        // UNKNOWN, so a bare negation would silently drop every row with no value in the
        // column even though such a row plainly does not match. The explicit IS NULL arm
        // keeps them. Mirrors RuleConditionSql in ReportSqlBuilder.cs - keep the two in step.
        case "not_starts_with": return condition.text ? `(${textSql} NOT LIKE ${sqlLikeLiteral(condition.text, "starts_with")} OR ${sql} IS NULL)` : null;
        case "ends_with": return condition.text ? `${textSql} LIKE ${sqlLikeLiteral(condition.text, "ends_with")}` : null;
        case "not_ends_with": return condition.text ? `(${textSql} NOT LIKE ${sqlLikeLiteral(condition.text, "ends_with")} OR ${sql} IS NULL)` : null;
        case "gt": return condition.values[0] ? `${compareSql} > ${comparisonValue(condition.values[0])}` : null;
        case "gte": return condition.values[0] ? `${compareSql} >= ${comparisonValue(condition.values[0])}` : null;
        case "lt": return condition.values[0] ? `${compareSql} < ${comparisonValue(condition.values[0])}` : null;
        case "lte": return condition.values[0] ? `${compareSql} <= ${comparisonValue(condition.values[0])}` : null;
        case "before": return condition.values[0] ? `${compareSql} < ${comparisonValue(condition.values[0])}` : null;
        case "after": return condition.values[0] ? `${compareSql} > ${comparisonValue(condition.values[0])}` : null;
        // BETWEEN is inclusive on both ends, matching SQL Server's own BETWEEN. Both bounds
        // are required - a half-filled range emits nothing rather than silently becoming a
        // one-sided comparison the user did not ask for.
        case "between": return condition.values[0] && condition.values[1]
            ? `${compareSql} BETWEEN ${comparisonValue(condition.values[0])} AND ${comparisonValue(condition.values[1])}`
            : null;
        case "not_between": return condition.values[0] && condition.values[1]
            ? `(${compareSql} IS NULL OR ${compareSql} NOT BETWEEN ${comparisonValue(condition.values[0])} AND ${comparisonValue(condition.values[1])})`
            : null;
        case "empty": return `(${sql} IS NULL OR NULLIF(LTRIM(RTRIM(${textSql})), '') IS NULL)`;
        case "not_empty": return `(${sql} IS NOT NULL AND NULLIF(LTRIM(RTRIM(${textSql})), '') IS NOT NULL)`;
        default: return null;
    }
}

/**
 * Compares a computed numeric expression (currently only a whole-minute difference)
 * against the condition's typed number(s).
 *
 * Only the numeric operators have a reading here, so anything else returns null and the
 * condition is dropped rather than emitting SQL that silently never matches. A bound that
 * is not a number is rejected for the same reason - and because the value would otherwise
 * be concatenated into the preview as-is.
 *
 * "ne" and "not_between" carry an IS NULL arm: DATEDIFF over a NULL operand is NULL, and a
 * row whose timestamps are missing plainly does not sit inside the range being excluded.
 *
 * Mirrors MinutesComparisonSql in ReportSqlBuilder.cs.
 */
function minutesComparisonSql(diffSql: string, condition: RuleCondition) {
    const numeric = (value: string | undefined) => {
        const trimmed = String(value ?? "").trim();
        return trimmed && /^-?\d+(\.\d+)?$/.test(trimmed) ? trimmed : null;
    };
    const first = numeric(condition.values[0]);
    const second = numeric(condition.values[1]);
    switch (condition.operator) {
        case "eq": return first === null ? null : `${diffSql} = ${first}`;
        case "ne": return first === null ? null : `(${diffSql} IS NULL OR ${diffSql} <> ${first})`;
        case "gt": return first === null ? null : `${diffSql} > ${first}`;
        case "gte": return first === null ? null : `${diffSql} >= ${first}`;
        case "lt": return first === null ? null : `${diffSql} < ${first}`;
        case "lte": return first === null ? null : `${diffSql} <= ${first}`;
        case "between": return first === null || second === null ? null : `${diffSql} BETWEEN ${first} AND ${second}`;
        case "not_between": return first === null || second === null
            ? null
            : `(${diffSql} IS NULL OR ${diffSql} NOT BETWEEN ${first} AND ${second})`;
        default: return null;
    }
}

// Walks one level, joining its members by their per-junction operators (AND binding
// tighter than OR - see splitRuleAndRuns). Nested groups recurse, so operator precedence
// is carried entirely by the parentheses this adds: every AND run of 2+ and every level of
// 2+ runs is wrapped, so an OR can never leak out into an enclosing AND.
//
// Deliberately identical to the pre-nesting behaviour for a flat, uniformly-joined list:
// 0 usable clauses -> null, 1 -> the bare clause (no parentheses), all-AND -> "(a AND b)",
// all-OR -> "(a OR b)". A group whose children all resolve to null collapses to null and is
// dropped by its parent, so an empty or unfinished group never emits "()" or a dangling
// AND/OR - and dropping it also drops its junction.
function ruleNodesExpression(context: ReportLogicContext, report: ReportConfig, nodes: RuleNode[] | null | undefined, logic: "and" | "or", useIds = false): string | null {
    const items = (nodes || [])
        .map((node) => {
            const clause = isRuleGroupNode(node)
                ? ruleNodesExpression(context, report, node.conditions, node.conditionLogic, useIds)
                : ruleConditionSql(context, report, node as RuleCondition, useIds);
            return clause ? { value: clause, join: ruleNodeJoin(node, logic) } : null;
        })
        .filter(Boolean) as { value: string; join: "and" | "or" }[];
    if (!items.length) return null;

    const runs = splitRuleAndRuns(items).map((run) => run.length === 1 ? run[0] : `(${run.join(" AND ")})`);
    return runs.length === 1 ? runs[0] : `(${runs.join(" OR ")})`;
}

function ruleConditionExpression(context: ReportLogicContext, report: ReportConfig, rule: CategorizationRule, useIds = false) {
    return ruleNodesExpression(context, report, rule.conditions, rule.conditionLogic, useIds);
}

/**
 * One-line logical outline of a rule's condition tree using column labels, e.g.
 * `Country Is Any Of (US) AND (State Equals CA OR State Equals NY)`. Used for the
 * SQL precedence comment and the rule header. Renders every node the user has
 * created, including incomplete ones, so the outline matches what is on screen.
 */
function ruleNodesOutline(context: ReportLogicContext, report: ReportConfig, nodes: RuleNode[] | null | undefined, logic: "and" | "or"): string {
    const items = (nodes || []).map((node) => {
        const text = isRuleGroupNode(node)
            ? (() => {
                const inner = ruleNodesOutline(context, report, node.conditions, node.conditionLogic);
                return inner ? `(${inner})` : "(empty group)";
            })()
            : ruleConditionOutline(context, report, node as RuleCondition);
        return { value: text, join: ruleNodeJoin(node, logic) };
    }).filter((item) => !!item.value);
    if (!items.length) return "";

    // Parenthesise AND runs exactly as ruleNodesExpression does, so what the user reads is
    // the precedence the SQL will actually have. Unlike the SQL builder this does NOT wrap
    // the outermost level - the reader already knows where the expression starts, and not
    // wrapping keeps a plain uniform rule reading as "a AND b AND c" like it always has.
    const runs = splitRuleAndRuns(items);
    if (runs.length === 1) return runs[0].join(" AND ");
    return runs.map((run) => run.length === 1 ? run[0] : `(${run.join(" AND ")})`).join(" OR ");
}

function ruleConditionOutline(context: ReportLogicContext, report: ReportConfig, condition: RuleCondition) {
    const column = findReportColumn(context, report, condition.column);
    const label = column?.label || condition.column || "(no column)";
    const operator = ruleOpDef(condition.operator);

    // The outline is the authoritative read-back - it has to describe the operands the SQL
    // will actually use, or a user cannot tell a column comparison from a comparison
    // against a value that happens to be spelled like a column name.
    const compareLabel = () => {
        const other = condition.compareColumn ? findReportColumn(context, report, condition.compareColumn) : null;
        return other?.label || condition.compareColumn || "(no column)";
    };
    // The outline is the authoritative read-back, so it must name the UNIT actually used and
    // any rounding actually applied - a comparison quietly rounded to the day must not read
    // identically to one compared exactly.
    const otherColumn = condition.compareColumn ? findReportColumn(context, report, condition.compareColumn) : null;
    if (condition.leftKind === "minutes_between") {
        const unit = resolveDifferenceUnit(column, otherColumn, condition.differenceUnit);
        const unitWord = unit === "minute" ? "Minutes" : unit === "hour" ? "Hours" : "Days";
        const subject = `${unitWord}${condition.absoluteMinutes ? " Apart" : ""} From ${label} To ${compareLabel()}`;
        if (operator.valueMode === "range") return `${subject} ${operator.label} ${condition.values[0] || "..."} And ${condition.values[1] || "..."}`;
        return `${subject} ${operator.label} ${condition.values[0] || "..."}`;
    }
    // "If blank, use" reads as part of the subject: "End Date (Blank = 12-31-2199) ...".
    const fallback = nullFallbackFor(condition, column);
    const subject = fallback ? `${label} (Blank = ${fallback.display})` : label;
    if (condition.compareTo === "today") return `${subject} ${operator.label} Today`;
    if (condition.compareTo === "column") {
        const tier = effectiveComparisonTier(column, otherColumn, condition.compareGranularity);
        const suffix = tier ? ` (by ${tier})` : "";
        return `${subject} ${operator.label} ${compareLabel()}${suffix}`;
    }
    // subject === label whenever no fallback applies, so these read exactly as before.
    if (operator.valueMode === "none") return `${label} ${operator.label}`;
    if (operator.valueMode === "text") return `${subject} ${operator.label} "${condition.text || "..."}"`;
    if (operator.valueMode === "single") return `${subject} ${operator.label} ${condition.values[0] || "..."}`;
    if (operator.valueMode === "range") return `${subject} ${operator.label} ${condition.values[0] || "..."} And ${condition.values[1] || "..."}`;
    return `${subject} ${operator.label} (${condition.values.length ? condition.values.join(", ") : "..."})`;
}

function ruleLogicOutline(context: ReportLogicContext, report: ReportConfig, rule: CategorizationRule) {
    return ruleNodesOutline(context, report, rule.conditions, rule.conditionLogic);
}

/** The rule's logic outline, safe to embed in a generated `--` SQL comment. */
function ruleLogicComment(context: ReportLogicContext, report: ReportConfig, rule: CategorizationRule) {
    return sqlCommentText(ruleLogicOutline(context, report, rule));
}

function buildCatRulesColumns(context: ReportLogicContext, report: ReportConfig, ruleSet?: RuleSet, useIds = false, categoryFallbackColumn: DataColumn | null = null) {
    if (!ruleSet?.rules.length) return [];
    const modelKey = ruleSetModelKey(context, report, ruleSet);
    const byField: Record<string, { field: ReturnType<typeof targetFieldDef>; whens: string[] }> = {};
    ruleSet.rules.forEach((rule, ruleIndex) => {
        if (rule.action !== "set") return;
        const result = ruleResultSql(context, report, rule, modelKey);
        if (!result) return;
        const expression = setRuleMatchExpression(context, report, rule, useIds, modelKey);
        if (!expression) return;
        const fieldKey = rule.targetField || "category";
        const field = targetFieldDef(context, fieldKey, report);
        byField[fieldKey] = byField[fieldKey] || { field, whens: [] };
        // Only rules that actually use nested groups get a precedence comment, so SQL
        // for existing flat rules stays byte-for-byte what it was before nesting.
        if (ruleHasNestedGroup(rule.conditions)) {
            byField[fieldKey].whens.push(`-- Rule ${ruleIndex + 1} logic: ${ruleLogicComment(context, report, rule)}`);
        }
        byField[fieldKey].whens.push(`WHEN ${expression} THEN ${result}`);
    });

    return Object.entries(byField).map(([fieldKey, entry]) => {
        const customElse = ruleSet.elseLabel.trim();
        const includeElse = ruleSet.includeElse !== false;
        // Column fallbacks go through resultTextSqlForColumn: the THEN branches are NVARCHAR,
        // so a non-text column here would force every label to its type and fail the query.
        const elseValue = includeElse
            ? customElse
                ? sqlLiteral(customElse)
                : fieldKey === "category"
                    ? categoryFallbackColumn ? relatedResultTextSql(categoryFallbackColumn) : sqlLiteral("Everything Else")
                    : entry.field.virtual ? entry.field.sql : relatedResultTextSql(entry.field)
            : "";
        const alias = fieldKey === "category" ? "Category" : `${safeSqlAlias(entry.field.sql)}_Result`;
        return {
            fieldKey,
            alias,
            expr: `CASE\n${entry.whens.map((line) => `      ${line}`).join("\n")}${elseValue ? `\n      ELSE ${elseValue}` : ""}\n    END`,
        };
    });
}

function buildRuleFilterClauses(context: ReportLogicContext, report: ReportConfig, ruleSet?: RuleSet, useIds = false) {
    const includeExpressions: string[] = [];
    const excludeExpressions: { expression: string; note: string }[] = [];
    const matchedSetExpressions: string[] = [];
    const modelKey = ruleSet ? ruleSetModelKey(context, report, ruleSet) : "";
    ruleSet?.rules.forEach((rule, ruleIndex) => {
        if (rule.action === "set") {
            // Same WHEN as the CASE: a column-sourced rule whose source column is blank did
            // not match, so with "hide unmatched" on the row is hidden like any other.
            if (ruleSet.includeElse === false && ruleHasResult(context, report, rule, modelKey)) {
                const setExpression = setRuleMatchExpression(context, report, rule, useIds, modelKey);
                if (setExpression) matchedSetExpressions.push(setExpression);
            }
            return;
        }
        const expression = ruleConditionExpression(context, report, rule, useIds);
        if (!expression) return;
        // Same rule as the CASE branches: nesting-only annotation, so flat rules are untouched.
        const note = ruleHasNestedGroup(rule.conditions) ? ` (Rule ${ruleIndex + 1} logic: ${ruleLogicComment(context, report, rule)})` : "";
        if (rule.action === "include") includeExpressions.push(expression);
        if (rule.action === "exclude") excludeExpressions.push({ expression, note });
    });

    const clauses: string[] = [];
    if (includeExpressions.length) {
        const includeClause = includeExpressions.length === 1 ? includeExpressions[0] : `(${includeExpressions.join(" OR ")})`;
        clauses.push(`${includeClause} -- Categorization include rule(s)`);
    }
    if (ruleSet?.includeElse === false && matchedSetExpressions.length) {
        const matchedSetClause = matchedSetExpressions.length === 1 ? matchedSetExpressions[0] : `(${matchedSetExpressions.join(" OR ")})`;
        clauses.push(`${matchedSetClause} -- Items without a matching rule are hidden`);
    }
    excludeExpressions.forEach((item) => {
        clauses.push(`NOT (${item.expression}) -- Categorization exclude rule${item.note}`);
    });
    return clauses;
}

function isCategorizationRuleReady(context: ReportLogicContext, report: ReportConfig, rule: CategorizationRule) {
    const hasValidCondition = !!ruleConditionExpression(context, report, rule);
    if (!hasValidCondition) return false;
    return rule.action === "set" ? ruleHasResult(context, report, rule) : true;
}

// --- Nested group validation --------------------------------------------------------
// Readiness above stays exactly as it was (a rule is ready when it produces at least
// one usable clause). These issues are advisory messages shown next to the builder so
// a half-built group is explained rather than silently dropped from the SQL.

export interface RuleNodeIssue {
    nodeId: string;
    kind: "emptyGroup" | "noColumn" | "noValue" | "tooDeep" | "badOperator" | "noCompareColumn" | "mismatchedColumns" | "sameColumn" | "valueSource" | "notADate" | "badBlankValue";
    message: string;
}

function ruleNodeLabelPath(path: number[]) {
    return path.map((index) => index + 1).join(".");
}

function collectRuleNodeIssues(context: ReportLogicContext, report: ReportConfig, nodes: RuleNode[] | null | undefined, depth: number, path: number[], issues: RuleNodeIssue[]) {
    (nodes || []).forEach((node, index) => {
        const nodePath = [...path, index];
        const label = ruleNodeLabelPath(nodePath);
        if (isRuleGroupNode(node)) {
            const children = node.conditions || [];
            if (!children.length) {
                issues.push({ nodeId: node.id, kind: "emptyGroup", message: `Group ${label} has no conditions. Add a condition inside it, or delete the group.` });
            } else if (!ruleLeafConditions(children).length) {
                issues.push({ nodeId: node.id, kind: "emptyGroup", message: `Group ${label} only contains empty groups, so it matches nothing. Add a condition inside it, or delete it.` });
            }
            if (depth + 1 > MAX_RULE_GROUP_DEPTH) {
                issues.push({ nodeId: node.id, kind: "tooDeep", message: `Group ${label} is nested deeper than ${MAX_RULE_GROUP_DEPTH} levels. Flatten it so the logic stays readable.` });
            }
            collectRuleNodeIssues(context, report, children, depth + 1, nodePath, issues);
            return;
        }
        const condition = node as RuleCondition;
        if (!condition.column) {
            issues.push({ nodeId: condition.id, kind: "noColumn", message: `Condition ${label} has no column picked yet, so it is ignored.` });
            return;
        }

        // --- Operand-mode issues -------------------------------------------------
        // These run before the generic "needs a value" check below, because a condition in
        // a new mode that is missing its second column also produces no SQL, and the
        // generic message would blame the wrong thing.
        const usesCompareColumn = condition.leftKind === "minutes_between" || condition.compareTo === "column";
        if (usesCompareColumn) {
            const left = findReportColumn(context, report, condition.column);
            const right = condition.compareColumn ? findReportColumn(context, report, condition.compareColumn) : null;
            if (!right) {
                issues.push({
                    nodeId: condition.id,
                    kind: "noCompareColumn",
                    message: condition.leftKind === "minutes_between"
                        ? `Condition ${label} needs both a From and a To time column, so it is ignored.`
                        : `Condition ${label} compares to a column but no column is picked, so it is ignored.`,
                });
                return;
            }
            if (left && columnsMatch(left, right)) {
                issues.push({
                    nodeId: condition.id,
                    kind: "sameColumn",
                    message: condition.leftKind === "minutes_between"
                        ? `Condition ${label} measures the time between ${left.label} and itself, which is always zero.`
                        : `Condition ${label} compares ${left.label} to itself, so it matches every row.`,
                });
            }
            if (left && condition.compareTo === "column" && columnDataTypeCategory(left) !== columnDataTypeCategory(right)) {
                issues.push({
                    nodeId: condition.id,
                    kind: "mismatchedColumns",
                    message: `Condition ${label} compares ${left.label} to ${right.label}. Those hold different kinds of value, so the comparison will never match.`,
                });
            }
        }

        // --- Date operands ------------------------------------------------------
        const dateColumn = findReportColumn(context, report, condition.column);
        if (condition.compareTo === "today" && condition.leftKind !== "minutes_between" && !isDateLikeColumn(dateColumn)) {
            issues.push({
                nodeId: condition.id,
                kind: "notADate",
                message: `Condition ${label} compares ${dateColumn?.label || "its column"} to today, but that column does not hold dates, so it is ignored.`,
            });
            return;
        }
        const fallbackIssue = conditionBlankValue(condition) !== undefined ? nullFallbackIssue(condition, dateColumn) : null;
        if (fallbackIssue) {
            issues.push({ nodeId: condition.id, kind: "badBlankValue", message: `Condition ${label}: ${fallbackIssue}` });
        }

        if (!ruleConditionSql(context, report, condition)) {
            const operator = ruleOpDef(condition.operator);
            issues.push({
                nodeId: condition.id,
                kind: "noValue",
                message: condition.leftKind === "minutes_between"
                    ? `Condition ${label} needs a number of minutes, so it is ignored.`
                    : condition.compareTo === "column"
                        ? `Condition ${label} cannot use ${ruleOpDef(condition.operator).label} against another column, so it is ignored.`
                        : condition.compareTo === "today"
                            ? `Condition ${label} cannot use ${ruleOpDef(condition.operator).label} against today, so it is ignored.`
                        : operator.valueMode === "text"
                            ? `Condition ${label} needs text to match, so it is ignored.`
                            : `Condition ${label} needs at least one value, so it is ignored.`,
            });
        }
    });
}

/** Ordered validation messages for one rule's condition tree. Empty means clean. */
function categorizationRuleIssues(context: ReportLogicContext, report: ReportConfig, rule: CategorizationRule): RuleNodeIssue[] {
    const issues: RuleNodeIssue[] = [];
    collectRuleNodeIssues(context, report, rule.conditions, 0, [], issues);
    if (!ruleConditionExpression(context, report, rule)) {
        issues.push({ nodeId: rule.id, kind: "emptyGroup", message: "This rule has no usable condition yet, so it will not generate SQL." });
    }
    const valueIssue = ruleValueSourceIssue(context, report, rule);
    if (valueIssue) issues.push({ nodeId: rule.id, kind: "valueSource", message: valueIssue });
    return issues;
}

/**
 * Why a column-sourced Set rule cannot produce its value, or null. Distinguishes "nothing
 * chosen" from "chosen but unusable", because the second happens to saved rules when the
 * catalog changes (a column marked sensitive, or moved off the rule set's model).
 */
function ruleValueSourceIssue(context: ReportLogicContext, report: ReportConfig, rule: CategorizationRule) {
    if (rule.action !== "set" || !ruleValueFromColumn(rule)) return null;
    if (!rule.valueColumn) return "Choose the column this rule should take its value from.";
    if (ruleValueColumn(context, report, rule)) return null;
    const anywhere = findReportColumn(context, report, rule.valueColumn);
    if (!anywhere) return "The column this rule takes its value from is no longer available. Choose another column.";
    if (!isRuleValueSourceEligible(anywhere)) return `"${anywhere.label}" can't be used as a rule value. Choose another column.`;
    return `"${anywhere.label}" is not on the same data model as this rule's conditions. Choose a column from that model.`;
}

/** Validation messages for every rule in a set, keyed by rule id. */
function ruleSetIssues(context: ReportLogicContext, report: ReportConfig, ruleSet?: RuleSet): Record<string, RuleNodeIssue[]> {
    const byRule: Record<string, RuleNodeIssue[]> = {};
    (ruleSet?.rules || []).forEach((rule) => {
        const issues = categorizationRuleIssues(context, report, rule);
        if (issues.length) byRule[rule.id] = issues;
    });
    return byRule;
}

function isRuleSetReady(context: ReportLogicContext, report: ReportConfig, ruleSet?: RuleSet) {
    if (!ruleSet) return false;
    return buildCatRulesColumns(context, report, ruleSet).length > 0 || buildRuleFilterClauses(context, report, ruleSet).length > 0;
}

// --- Metric filter condition trees ------------------------------------------------
// The include ("Inclusion Criteria") and exclude ("Exclusion Criteria") filters
// are condition trees with the same shape as a Categorization rule's, so everything below
// delegates to the shared rule-node helpers rather than re-deriving AND/OR precedence.
// The one adaptation needed is scope: a Categorization condition resolves its column
// against every model attached to the report, whereas a metric filter must only ever see
// its own section's model. metricFilterScope() supplies that by handing the shared helpers
// a report narrowed to one model - which is why no helper below needs a second signature.

export type MetricFilterMode = "filter" | "excludeFilter";

/** A report narrowed to one section's model, so shared rule helpers resolve the right columns. */
function metricFilterScope(report: ReportConfig, section: MetricSection): ReportConfig {
    return { ...report, models: section.model ? [section.model] : [] };
}

/** The column pool a metric filter condition may pick from: every visible column on the model. */
function metricFilterColumns(context: ReportLogicContext, section: MetricSection) {
    return allColumnsForModel(context, section.model);
}

/**
 * The root AND/OR default for one side. The include side defaults to AND (narrow further
 * with each condition) and the exclude side to OR (remove rows matching any condition),
 * which is what "leave out rows where A or B" reads as once the tree is negated as a whole.
 *
 * The per-side default matters for configs saved before these fields existed: falling back
 * to a single shared default would make an old metric and a new one behave differently from
 * the same user actions once a second condition is added.
 */
function metricFilterDefaultLogic(mode: MetricFilterMode): "and" | "or" {
    return mode === "filter" ? "and" : "or";
}

function metricFilterLogic(section: MetricSection, mode: MetricFilterMode): "and" | "or" {
    const stored = mode === "filter" ? section.filterLogic : section.excludeFilterLogic;
    if (stored === "or") return "or";
    if (stored === "and") return "and";
    return metricFilterDefaultLogic(mode);
}

/**
 * The condition tree for one side of a metric's filters. When no tree is stored but the
 * legacy single-column fields are set, one equivalent condition is synthesised on the fly -
 * that is the whole backward-compatibility story for reads. The synthesised node carries a
 * stable id derived from the section so React keys and issue ids stay put across renders.
 */
/**
 * The id given to a condition synthesised from the legacy fields. Stable across renders, and
 * recognisable later: a node carrying it was never authored in the new builder, so its
 * operator is grandfathered rather than validated against the column's data type.
 */
function legacyMetricFilterNodeId(section: MetricSection, mode: MetricFilterMode) {
    return `${section.id}-legacy-${mode}`;
}

function metricFilterNodes(section: MetricSection, mode: MetricFilterMode): RuleNode[] {
    const stored = mode === "filter" ? section.filterConditions : section.excludeFilterConditions;
    if (stored && stored.length) return stored;

    const column = mode === "filter" ? section.filterColumn : section.excludeFilterColumn;
    const values = (mode === "filter" ? section.filterValues : section.excludeFilterValues) || [];
    if (!column) return [];

    return [{
        id: legacyMetricFilterNodeId(section, mode),
        type: "condition",
        column,
        // BOTH sides synthesise "Is Any Of", including the exclude side, because the exclude
        // filter negates its whole tree (see metricFilterClause) rather than each condition.
        // The legacy field pair means "col IN (values)" for the include filter and
        // "col NOT IN (values)" for the exclude filter, and NOT (col IN (values)) is exactly
        // that second meaning. Synthesising "Is None Of" here instead would double-negate and
        // invert every exclusion ever saved.
        operator: "in",
        values: [...values],
        text: "",
    }];
}

/**
 * True when a tree is still expressible in the legacy fields: exactly one "Is Any Of" leaf
 * on one column. Only then can the legacy pair be written alongside the tree without
 * misrepresenting it.
 *
 * The operator to look for is "in" on BOTH sides, for the same reason metricFilterNodes
 * synthesises "in" on both: the exclude filter negates its whole tree, so a single
 * "col IN (values)" tree is what the legacy excludeFilterColumn/excludeFilterValues pair
 * has always meant. A single "Is None Of" tree is deliberately NOT mirrored - under
 * negation it means "col IN (values)", the opposite of what a legacy reader would make of
 * those fields, so the pair is cleared instead.
 */
function metricFilterLegacyEquivalent(context: ReportLogicContext, section: MetricSection, nodes: RuleNode[]): RuleCondition | null {
    if (nodes.length !== 1) return null;
    const node = nodes[0];
    if (isRuleGroupNode(node)) return null;
    const condition = node as RuleCondition;
    if (condition.operator !== "in" || !condition.column) return null;
    if (condition.text) return null;
    // The legacy fields have always held a column KEY, while a condition may address its
    // column by DataModelColumnId. Resolve back to the key so a legacy reader still finds
    // the column; if it cannot be resolved, do not mirror at all.
    const column = metricFilterColumns(context, section).find((item) => columnMatchesKey(item, condition.column));
    if (!column) return null;
    return { ...condition, column: column.key };
}

/**
 * Stores a condition tree on a section, keeping the legacy fields truthful: mirrored when
 * the tree is still a single-column IN/NOT IN, cleared when it is anything richer. Clearing
 * is the safe half - a consumer that only understands the legacy shape then applies no
 * filter rather than applying one condition out of several and over-counting.
 */
function setMetricFilterNodes(context: ReportLogicContext, section: MetricSection, mode: MetricFilterMode, nodes: RuleNode[]): MetricSection {
    const legacy = metricFilterLegacyEquivalent(context, section, nodes);
    const legacyFields = mode === "filter"
        ? { filterColumn: legacy?.column || "", filterValues: legacy ? [...legacy.values] : [] }
        : { excludeFilterColumn: legacy?.column || "", excludeFilterValues: legacy ? [...legacy.values] : [] };
    const treeFields = mode === "filter"
        ? { filterConditions: nodes }
        : { excludeFilterConditions: nodes };
    return { ...section, ...legacyFields, ...treeFields };
}

function setMetricFilterLogic(section: MetricSection, mode: MetricFilterMode, logic: "and" | "or"): MetricSection {
    return mode === "filter" ? { ...section, filterLogic: logic } : { ...section, excludeFilterLogic: logic };
}

/** True when this side of the filters is switched on at all (tree stored or legacy column set). */
function metricFilterEnabled(section: MetricSection, mode: MetricFilterMode) {
    return metricFilterNodes(section, mode).length > 0;
}

/**
 * The WHERE fragment for one side of a metric's filters, or null when nothing usable is
 * configured. The include side is the expression itself; the exclude side is its negation.
 *
 * That negation goes through CASE rather than a bare NOT (...) because of NULLs: a
 * predicate over a NULL column evaluates to UNKNOWN, and NOT UNKNOWN is still UNKNOWN, so a
 * bare NOT would drop every row whose filtered column is NULL even though it plainly does
 * not match the exclusion. CASE has no three-valued behaviour - an UNKNOWN condition takes
 * the ELSE branch - so those rows evaluate to 0 and are kept, exactly as nullSafeNotIn did
 * for the single-column filter this replaces. Mirrors MetricFilterClause in
 * ReportSqlBuilder.cs; the two must stay identical.
 */
function metricFilterClause(context: ReportLogicContext, report: ReportConfig, section: MetricSection, mode: MetricFilterMode, useIds = false) {
    const nodes = metricFilterNodes(section, mode);
    if (!nodes.length) return null;
    const expression = ruleNodesExpression(context, metricFilterScope(report, section), nodes, metricFilterLogic(section, mode), useIds);
    if (!expression) return null;
    return mode === "filter" ? expression : `(CASE WHEN ${expression} THEN 1 ELSE 0 END) = 0`;
}

function buildMetricFilterClauses(context: ReportLogicContext, report: ReportConfig, section: MetricSection, column?: DataColumn, useIds = false) {
    if (!section.model) return [];
    const modelKey = section.model;
    const where: string[] = [];
    if (column && section.include.length) where.push(inClause(context, modelKey, column, section.include, useIds));
    if (column && section.exclude.length) where.push(notInClause(context, modelKey, column, section.exclude, useIds));
    const includeClause = metricFilterClause(context, report, section, "filter", useIds);
    if (includeClause) where.push(includeClause);
    const excludeClause = metricFilterClause(context, report, section, "excludeFilter", useIds);
    if (excludeClause) where.push(excludeClause);
    return where;
}

/**
 * Validation messages for one side of a metric's filters. Reuses the Categorization node
 * walker for the structural checks (no column, no value, empty group, too deep) and adds
 * the one check that only exists here: an operator that is not legal for its column's data
 * type. That can only arise from a config saved before the column was reclassified, since
 * the picker never offers an illegal operator.
 */
function metricFilterIssues(context: ReportLogicContext, report: ReportConfig, section: MetricSection, mode: MetricFilterMode): RuleNodeIssue[] {
    const nodes = metricFilterNodes(section, mode);
    if (!nodes.length) return [];

    const scope = metricFilterScope(report, section);
    const issues: RuleNodeIssue[] = [];
    collectRuleNodeIssues(context, scope, nodes, 0, [], issues);

    const legacyNodeId = legacyMetricFilterNodeId(section, mode);
    ruleLeafConditions(nodes).forEach((condition) => {
        if (!condition.column) return;
        // A condition synthesised from the legacy fields is exempt. It generates exactly the
        // SQL the old single-column filter did, so flagging it - and through
        // metricFilterHasInvalidOperator, refusing to call the metric ready - would break a
        // config that has always worked, purely because its column is classified as numeric
        // or date and "Is Any Of" is not offered for those types in the new picker.
        if (condition.id === legacyNodeId) return;
        const column = findReportColumn(context, scope, condition.column);
        if (!column || catalogOperatorAllowed(column, condition.operator, context.ruleOperators)) return;
        issues.push({
            nodeId: condition.id,
            kind: "badOperator",
            message: `"${ruleOpDef(condition.operator).label}" cannot be used on ${column.label}, which is a ${columnDataTypeCategory(column)} column. Pick a different operator.`,
        });
    });

    if (!metricFilterClause(context, report, section, mode)) {
        issues.push({
            nodeId: `${section.id}-${mode}`,
            kind: "emptyGroup",
            message: mode === "filter"
                ? "This filter has no usable condition yet, so no rows are being narrowed down."
                : "This filter has no usable condition yet, so no rows are being left out.",
        });
    }

    return issues;
}

/**
 * Readable one-line description of one side of a metric's filters, e.g.
 * `Disposition Is Any Of (Answered, Completed) AND Queue Contains Text "Sales"`. Shares
 * ruleNodesOutline with the Categorization builder, so the parenthesisation shown matches
 * the precedence the generated SQL actually has.
 */
function metricFilterOutline(context: ReportLogicContext, report: ReportConfig, section: MetricSection, mode: MetricFilterMode) {
    const nodes = metricFilterNodes(section, mode);
    if (!nodes.length) return "";
    return ruleNodesOutline(context, metricFilterScope(report, section), nodes, metricFilterLogic(section, mode));
}

/** Every metric filter issue for a section, both sides, in display order. */
function metricFilterIssuesForSection(context: ReportLogicContext, report: ReportConfig, section: MetricSection): RuleNodeIssue[] {
    return [
        ...metricFilterIssues(context, report, section, "filter"),
        ...metricFilterIssues(context, report, section, "excludeFilter"),
    ];
}

/**
 * Blocking validation: an operator/data-type mismatch is the only filter problem that must
 * stop a save. Everything else the walker reports (a half-finished condition, an empty
 * group) already degrades to "emits no SQL", which the builder has always allowed while a
 * user is mid-edit.
 */
function metricFilterHasInvalidOperator(context: ReportLogicContext, report: ReportConfig, section: MetricSection) {
    return metricFilterIssuesForSection(context, report, section).some((issue) => issue.kind === "badOperator");
}

function simulateRuleSet(context: ReportLogicContext, report: ReportConfig, ruleSet: RuleSet, inputs: Record<string, string>) {
    const includeRules = ruleSet.rules.filter((rule) => rule.action === "include");
    const excludeRules = ruleSet.rules.filter((rule) => rule.action === "exclude");
    const includedByRules = !includeRules.length || includeRules.some((rule) => simulateRuleMatches(inputs, rule));
    const excludedByRules = excludeRules.some((rule) => simulateRuleMatches(inputs, rule));
    const outputTargets = Array.from(new Set(ruleSet.rules.filter((rule) => rule.action === "set").map((rule) => rule.targetField || "category")));
    const outputs = outputTargets.map((targetKey) => {
        const field = targetFieldDef(context, targetKey, report);
        // A column-sourced rule writes the sample value of its source column, and - exactly as
        // in the SQL - does not match when that sample is blank, so the next rule gets a turn.
        const simulatedResult = (rule: CategorizationRule) => {
            if (!ruleValueFromColumn(rule)) return ruleResultValue(rule);
            const column = ruleValueColumn(context, report, rule);
            return column ? (inputs[column.key] || "").trim() : "";
        };
        const matchingRule = ruleSet.rules.find((rule) => rule.action === "set" && (rule.targetField || "category") === targetKey && simulatedResult(rule) && simulateRuleMatches(inputs, rule));
        const fallback = ruleSet.includeElse === false ? "(Not shown)" : ruleSet.elseLabel.trim() || inputs[targetKey] || "(Existing Value)";
        return { label: field.label, value: matchingRule ? simulatedResult(matchingRule) : fallback };
    });

    return {
        included: includedByRules && !excludedByRules,
        outputs: outputs.length ? outputs : [{ label: "Category", value: ruleSet.includeElse === false ? "(Not shown)" : ruleSet.elseLabel.trim() || "(Existing Value)" }],
    };
}

function isMetricReady(context: ReportLogicContext, report: ReportConfig, section: MetricSection) {
    if (!section.model || !section.breakdownColumn) return false;
    const column = columnsForModel(context, section.model).find((item) => columnMatchesKey(item, section.breakdownColumn));
    if (!column) return false;
    if (usesDetailRuleSetList(section)) {
        // Full Row Detail with several rule sets: every selected rule set must be usable.
        if (selectedDetailRuleSetIds(section).some((id) => !isRuleSetReady(context, report, report.bucketRuleSets.find((set) => set.id === id)))) {
            return false;
        }
    } else if (usesCombinedRuleSets(section)) {
        // Combined rule sets: everything the server checks before building the query.
        if (!isCombinedRuleSetsReady(context, report, section)) return false;
    } else if (section.breakdownSource === "ruleSet" && !isRuleSetReady(context, report, report.bucketRuleSets.find((set) => set.id === section.ruleSetId))) {
        return false;
    }
    if (section.displayAs !== "detail" && !section.measure?.fieldId && section.countMode === "distinct" && !resolveCountDistinctColumn(context, section.model, section.countMode, section.countDistinctColumn)) {
        return false;
    }
    // Calculated fields: a measure that cannot be built, or a detail column whose field is
    // gone / incomplete / on another model, would make the server refuse the query.
    if (measureIssue(context, report, section)) return false;
    if (detailCalculatedColumns(context, report, section).some((item) => !item.expr)) return false;
    if (detailConstantIssues(section).length) return false;
    // An operator that its column's data type does not support is the one filter problem
    // that must block a save: unlike a half-typed condition, it cannot be reached from the
    // picker and would generate SQL nobody asked for if the type were later trusted.
    if (metricFilterHasInvalidOperator(context, report, section)) {
        return false;
    }
    // An incomplete Period Date rule would silently re-date rows, and the server refuses
    // to run one - so it blocks readiness the same way an invalid operator does.
    if (!isPeriodDateReady(context, report, section)) {
        return false;
    }
    if (section.displayAs === "otp") {
        return !!buildLateConditionSql(context, section);
    }
    return true;
}

type ComputedColumn = { fieldKey: string; alias: string; expr: string };

function virtualCategoryColumn(computedColumns: ComputedColumn[]) {
    return computedColumns.find((item) => item.fieldKey === "category") || null;
}

export {
    ruleResultValue,
    ruleValueFromColumn,
    ruleSetModelKey,
    ruleValueColumn,
    ruleHasResult,
    ruleResultSql,
    ruleResultLabel,
    ruleValueNotBlankCondition,
    setRuleMatchExpression,
    ruleValueSourceIssue,
    ruleConditionSql,
    ruleNodesExpression,
    ruleConditionExpression,
    ruleNodesOutline,
    ruleConditionOutline,
    ruleLogicOutline,
    ruleLogicComment,
    buildCatRulesColumns,
    buildRuleFilterClauses,
    isCategorizationRuleReady,
    ruleNodeLabelPath,
    collectRuleNodeIssues,
    categorizationRuleIssues,
    ruleSetIssues,
    isRuleSetReady,
    metricFilterScope,
    metricFilterColumns,
    metricFilterDefaultLogic,
    metricFilterLogic,
    legacyMetricFilterNodeId,
    metricFilterNodes,
    metricFilterLegacyEquivalent,
    setMetricFilterNodes,
    setMetricFilterLogic,
    metricFilterEnabled,
    metricFilterClause,
    buildMetricFilterClauses,
    metricFilterIssues,
    metricFilterOutline,
    metricFilterIssuesForSection,
    metricFilterHasInvalidOperator,
    simulateRuleSet,
    isMetricReady,
    virtualCategoryColumn,
};
export type { ComputedColumn };
